import React, { useState } from 'react';
import { organizationAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorUtils';
import { Modal } from './ui/Modal';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Select } from './ui/Select';

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  onInviteSent: () => void;
}

export const InviteModal: React.FC<InviteModalProps> = ({
  isOpen, onClose, organizationId, organizationName, onInviteSent,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Email is required'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('Please enter a valid email address'); return; }
    setLoading(true);
    try {
      await organizationAPI.sendInvitation(organizationId, { email, role });
      setEmail(''); setRole('member'); onInviteSent(); onClose();
    } catch (err: unknown) { setError(getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const handleClose = () => { if (!loading) { setEmail(''); setRole('member'); setError(''); onClose(); } };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invite Member" size="md" variant="sheet">
      <p className="text-sm text-text-secondary m-0 mb-6">
        Send an invitation to join <strong className="text-accent font-semibold">{organizationName}</strong>
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <Input
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@example.com"
            disabled={loading}
            helperText="An invitation email will be sent to this address"
          />
        </div>

        <div>
          <Select id="inviteRole" value={role} onChange={(e) => setRole(e.target.value as 'member' | 'admin')} disabled={loading} size="md" label="Role"
            helperText={role === 'admin' ? 'Can manage hierarchy settings and invite members' : 'Can view and manage tasks'}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </Select>
        </div>

        {error && (
          <div className="py-2 px-3 bg-danger/10 border border-danger rounded-lg text-danger text-sm dark:bg-danger/20 dark:text-[var(--theme-danger-muted)]" role="alert">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4 border-t border-border-light">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
